import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { PermissionsContext } from './complexContexts';
import { logger } from '../utils/logger';

export type UserRole = 'admin' | 'catalog-owner' | 'viewer' | 'delegate' | 'legacy-manager';
export type SubscriptionTier = 'creator' | 'commercial';

/**
 * Delegation entry can be either:
 * - Old format: just a catalog ID string
 * - New format: object with catalogId and permission level
 */
export type DelegationEntry = string | { catalogId: string; permission: 'view' | 'full' };

export interface UserPermissions {
  role: UserRole;
  subscriptionTier?: SubscriptionTier;
  assignedId?: string;        // ALI ID (e.g., "JD-3K7") - used for display/reference
  userId?: string;            // Cognito user ID (sub claim) - used for ownership checks
  delegatedCatalogs: DelegationEntry[];
  legacyAssignments: string[];
}

export interface PermissionsContextType {
  permissions: UserPermissions | null;
  isLoading: boolean;
  hasPermission: (action: string, resource?: Record<string, unknown>) => boolean;
  canViewCatalog: (catalogId: string, visibility: 'public' | 'private') => boolean;
  canEditCatalog: (catalogId: string, ownerId: string) => boolean;
  canBulkOperation: (itemCount: number) => boolean;
  refreshPermissions: () => Promise<void>;
}





const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, getIdToken, isLoading: authLoading } = useAuth();
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  // Start with isLoading=true because permissions are unknown at mount time
  // This prevents RoleProtectedRoute from prematurely redirecting before permissions load
  const [isLoading, setIsLoading] = useState(true);

  const parseUserAttributes = (token: string): UserPermissions | null => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));

      // Map Cognito groups to roles - prioritize higher permissions
      const cognitoGroups = payload['cognito:groups'] || [];
      let role: UserRole = 'viewer'; // default

      if (cognitoGroups.includes('admin')) {
        role = 'admin';
      } else if (cognitoGroups.includes('catalog-owner') || cognitoGroups.includes('commercial')) {
        role = 'catalog-owner'; // catalog-owner takes priority over delegate
      } else if (cognitoGroups.includes('delegate')) {
        role = 'delegate';
      } else if (cognitoGroups.includes('viewer')) {
        role = 'viewer';
      }

      // Fallback to custom:userRole for cognito-local compatibility
      // cognito-local doesn't properly set cognito:groups, so check custom attribute
      if (role === 'viewer' && payload['custom:userRole']) {
        const customRole = payload['custom:userRole'];
        if (customRole === 'catalog_owner' || customRole === 'commercial') {
          role = 'catalog-owner';
        } else if (customRole === 'basic') {
          role = 'viewer';
        }
      }

      // Derive subscriptionTier: prefer explicit Cognito attribute, fall back to group
      let subscriptionTier: SubscriptionTier | undefined = payload['custom:subscriptionTier'];
      if (!subscriptionTier) {
        if (cognitoGroups.includes('commercial')) {
          subscriptionTier = 'commercial';
        } else if (cognitoGroups.includes('catalog-owner')) {
          subscriptionTier = 'creator';
        }
      }

      // Cognito-local fallback: when cognito-local fails to populate groups,
      // derive from custom:userRole instead
      if (!subscriptionTier && payload['custom:userRole']) {
        const customRole = payload['custom:userRole'];
        if (customRole === 'commercial') {
          subscriptionTier = 'commercial';
        } else if (customRole === 'catalog_owner') {
          subscriptionTier = 'creator';
        }
      }

      return {
        role,
        subscriptionTier,
        assignedId: payload['custom:assignedId'],
        userId: payload.sub,  // Cognito user ID for ownership checks
        delegatedCatalogs: payload['custom:delegatedCatalogs'] ?
          JSON.parse(payload['custom:delegatedCatalogs']) : [],
        legacyAssignments: payload['custom:legacyAssignments'] ?
          JSON.parse(payload['custom:legacyAssignments']) : [],
      };
    } catch (error) {
      logger.error('Error parsing user attributes', error);
      return null;
    }
  };

  const refreshPermissions = useCallback(async () => {
    // If auth is still loading, keep permissions loading too.
    // This prevents RoleProtectedRoute from seeing isLoading=false + permissions=null
    // and redirecting to /home before auth has had a chance to authenticate.
    if (authLoading) {
      setIsLoading(true);
      return;
    }

    if (!isAuthenticated || !user) {
      setPermissions(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const token = await getIdToken();
      if (token) {
        const userPermissions = parseUserAttributes(token);
        setPermissions(userPermissions);
      }
    } catch (error: unknown) {
      logger.error('Error refreshing permissions', error);
      setPermissions(null);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user, getIdToken, authLoading]);

  useEffect(() => {
    refreshPermissions();
  }, [isAuthenticated, user, authLoading, refreshPermissions]);

  /**
   * Check if user has any delegation for the given catalog.
   * Supports both old format (string array) and new format (object with permission).
   */
  const hasDelegationFor = (delegations: DelegationEntry[], catalogId: unknown): boolean => {
    if (!catalogId || typeof catalogId !== 'string') return false;
    return delegations.some(d => {
      if (typeof d === 'string') {
        // Old format: just catalog ID strings
        return d === catalogId;
      }
      // New format: object with catalogId and permission
      return d.catalogId === catalogId;
    });
  };

  /**
   * Check if user has a specific permission level for the given catalog.
   * Only works with new format; old format returns false for edit/delete permissions.
   */
  const hasDelegationWithPermission = (
    delegations: DelegationEntry[],
    catalogId: unknown,
    requiredPermission: 'view' | 'full'
  ): boolean => {
    if (!catalogId || typeof catalogId !== 'string') return false;
    return delegations.some(d => {
      if (typeof d === 'string') {
        // Old format doesn't include permission level - only allow view operations
        return false;
      }
      // New format: check if catalogId matches and permission is sufficient
      return d.catalogId === catalogId && d.permission === requiredPermission;
    });
  };

  const hasPermission = (action: string, resource?: Record<string, unknown>): boolean => {
    if (!permissions) return false;

    switch (permissions.role) {
      case 'admin':
        return true;

      case 'catalog-owner':
        switch (action) {
          case 'ViewCatalog':
          case 'CreateEntry':
          case 'EditEntry':
          case 'DeleteEntry':
          case 'ShareCatalog':
          case 'DelegateAccess':
          case 'ViewAnalytics':
          case 'BulkOperations':
            // Use userId (Cognito sub) for ownership check, NOT assignedId (ALI ID)
            return resource?.ownerId === permissions.userId ||
                   permissions.legacyAssignments.includes(resource?.catalogId);
          default:
            return false;
        }

      case 'viewer':
        return action === 'ViewCatalog' && resource?.visibility === 'public';

      case 'delegate':
        switch (action) {
          case 'ViewCatalog':
            return resource?.visibility === 'public' ||
                   hasDelegationFor(permissions.delegatedCatalogs, resource?.catalogId);
          case 'CreateEntry':
          case 'EditEntry':
          case 'DeleteEntry':
            // Only delegates with 'full' permission can create/edit/delete
            return hasDelegationWithPermission(permissions.delegatedCatalogs, resource?.catalogId, 'full');
          case 'ManageLabels':
            return hasDelegationFor(permissions.delegatedCatalogs, resource?.catalogId);
          default:
            return false;
        }

      case 'legacy-manager':
        switch (action) {
          case 'ViewCatalog':
          case 'CreateEntry':
          case 'EditEntry':
          case 'DeleteEntry':
          case 'ShareCatalog':
          case 'DelegateAccess':
          case 'ViewAnalytics':
          case 'BulkOperations':
            return permissions.legacyAssignments.includes(resource?.catalogId);
          default:
            return false;
        }

      default:
        return false;
    }
  };

  const canViewCatalog = (catalogId: string, visibility: 'public' | 'private'): boolean => {
    return hasPermission('ViewCatalog', { catalogId, visibility });
  };

  const canEditCatalog = (catalogId: string, ownerId: string): boolean => {
    return hasPermission('EditEntry', { catalogId, ownerId });
  };

  const canBulkOperation = (itemCount: number): boolean => {
    if (!permissions) return false;

    if (permissions.role === 'admin') return true;

    if (permissions.role === 'catalog-owner') {
      if (permissions.subscriptionTier === 'creator' && itemCount > 100) {
        return false;
      }
      return true;
    }

    return permissions.role === 'legacy-manager';
  };

  return (
    <PermissionsContext.Provider value={{
      permissions,
      isLoading,
      hasPermission,
      canViewCatalog,
      canEditCatalog,
      canBulkOperation,
      refreshPermissions,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
};

export { PermissionsProvider };
